import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { apiTokenRequest } from '../../lib/msalConfig.js';
import { forgetSignedOut, signedOutOnPurpose, takeAutoSignInTurn } from './signInMemory.js';

type AutoSignIn = 'signed_in' | 'redirecting' | 'needs_them';

/**
 * Signs them in without the card when Microsoft still knows them: silently first, then by a
 * trip to Microsoft that usually comes straight back. The card shows only after they signed
 * out themselves, or when Microsoft needs them to do something.
 */
export const useLoginPageLogic = () => {
  const { instance, inProgress } = useMsal();
  // MSAL's silent sign-in loads this app in a hidden frame; that copy must not start its own.
  const auto = !signedOutOnPurpose() && window.self === window.top;

  const attempt = useQuery({
    queryKey: ['auth', 'auto-sign-in'],
    queryFn: async (): Promise<AutoSignIn> => {
      try {
        // On success MSAL adds the account and the app swaps to the signed-in view.
        await instance.ssoSilent(apiTokenRequest);
        return 'signed_in';
      } catch {
        if (!takeAutoSignInTurn()) return 'needs_them';
        await instance.loginRedirect(apiTokenRequest);
        return 'redirecting';
      }
    },
    enabled: auto && inProgress === InteractionStatus.None,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const signIn = (): void => {
    forgetSignedOut();
    void instance.loginRedirect(apiTokenRequest);
  };

  return {
    showCard: !auto || attempt.isError || attempt.data === 'needs_them',
    signIn,
  };
};
